import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { omit, get } from "lodash";
import { useFormik } from "formik";

import ApplicantDetails from "Components/ApplicantDetails";
import Stepper from "common/Stepper";
import AddressForPower from "Components/AddressForPower";
import BreadCrumb from "Components/Common/BreadCrumb";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import UtilitiesFields from "pages/Consumer/OtherApplications/UtilitiesFields";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import { applicationTypes } from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import { otherUtilityApplicationOptions, getOptionValue } from "common/enums";
import { myOtherRequestRoute } from "Routes/constants";

import { getPermanentConnectionSchema } from "utils/otherUtilitiesValidationSchema";
import {
  generateFormData,
  filterEmptyStrings,
  convertDocumentKeys,
  resetFieldValue,
} from "utils";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

import { useLazyGetOtherApplicationDetailQuery } from "apis/otherApplicationDetails";
import { useFloorQuery } from "apis/Floor";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import { usePostOtherUtilityApplicationDetailsMutation } from "apis/otherApplicationDetails";

const temporaryConnectionValues = [
  { setKey: "id", getKey: "id", defaultValue: 0 },
  { setKey: "groupId", getKey: "groupId", defaultValue: "" },
  { setKey: "applicant.id", getKey: "applicant.id", defaultValue: 0 },
  {
    setKey: "authorizedPerson.id",
    getKey: "authorizedPerson.id",
    defaultValue: 0,
  },
  { setKey: "utilityAddress.id", getKey: "utilityAddress.id", defaultValue: 0 },
  { setKey: "declaration.id", getKey: "declaration.id", defaultValue: 0 },
  {
    setKey: "waterSewageDemand.waterDemandId",
    getKey: "waterDemand.id",
    defaultValue: 0,
  },
  {
    setKey: "waterSewageDemand.sewageDemandId",
    getKey: "sewageDemand.id",
    defaultValue: 0,
  },
  {
    setKey: "dcsDemand.id",
    getKey: "dcsDemand.id",
    defaultValue: 0,
  },
  { setKey: "declaration.id", getKey: "declaration.id", defaultValue: 0 },
  { setKey: "documents", getKey: "documents", defaultValue: [] },
];

const OtherUtilityTemporaryConnection = ({
  editApplicationQuery,
  handleClose = () => {},
  t,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const navigate = useNavigate();

  const user = useSelector((state) => state.Login.user);

  const [
    getApplicationData,
    {
      data: { data: applicationData } = {},
      isLoading: isApplicationDataLoading,
    },
  ] = useLazyGetOtherApplicationDetailQuery();
  const { data: { data: floors } = {} } = useFloorQuery();
  const { data: { data: { items: applicantTypes } = {} } = {} } =
    useApplicantTypeQuery();
  const [submitApplication, { isLoading }] =
    usePostOtherUtilityApplicationDetailsMutation();

  const floorOptions = useMemo(
    () =>
      floors?.map((floor) => ({ label: floor.name, value: floor.id })) ?? [],
    [floors]
  );

  const initialValues = useMemo(
    () => ({
      id: applicationData?.id ?? 0,
      utilities:
        otherUtilityApplicationOptions.filter(({ value }) =>
          applicationData?.utilities?.includes(value)
        ) ?? [],
      applicationTypeId: applicationTypes["Temporary Connection"],
      groupId: applicationData?.groupId ?? "",
      applicant: {
        id: applicationData?.applicant?.id ?? 0,
        applicantTypeId: applicationData?.applicant?.applicantType?.id ?? "",
        applicantName: applicationData?.applicant?.applicantName ?? "",
        panNumber: applicationData?.applicant?.panNumber ?? "",
        gstNumber: applicationData?.applicant?.gstNumber ?? "",
        occupancyTypeId: applicationData?.applicant?.occupancyType?.id ?? "",
        addressType: applicationData?.applicant?.addressType ?? "",
        isGSTRegistered: applicationData?.applicant?.isGSTRegistered ?? "",
        correspondenceAddress:
          applicationData?.applicant?.correspondenceAddress ?? "",
        city: applicationData?.applicant?.city ?? "",
        pinCode: applicationData?.applicant?.pinCode ?? "",
        email: applicationData?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: applicationData?.applicant?.mobileNumber ?? "",
        landLine: applicationData?.applicant?.landLine ?? "",
        alternateEmail: applicationData?.applicant?.alternateEmail
          ? applicationData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          applicationData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },

      utilityAddress: {
        id: applicationData?.utilityAddress?.id ?? 0,
        premiseTypeId: applicationData?.utilityAddress?.premiseType?.id ?? "",
        dcsDomesticPremiseType:
          applicationData?.utilityAddress?.dcsDomesticPremiseType ?? "",
        dcsIndividualPremiseType:
          applicationData?.utilityAddress?.dcsIndividualPremiseType ?? "",
        premiseUsagePurpose:
          applicationData?.utilityAddress?.premiseUsagePurpose ?? "",
        premisesAreaId: applicationData?.utilityAddress?.premiseArea?.id ?? "",
        zoneName: applicationData?.utilityAddress?.zone?.name ?? "",
        otherZone: "",
        blockName: applicationData?.utilityAddress?.block?.name ?? "",
        otherBlock: "",
        roadName: applicationData?.utilityAddress?.road?.name ?? "",
        otherRoad: "",
        buildingName: applicationData?.utilityAddress?.building?.name ?? "",
        otherBuilding: "",
        floors: [],
        unitName: applicationData?.utilityAddress?.unitName ?? "",
        nearestLandmark: applicationData?.utilityAddress?.nearestLandmark ?? "",
        isBPL: applicationData?.utilityAddress?.isBPL ?? "",
        // isRural: applicationData?.utilityAddress?.isRural ?? "",
      },
      totalBuildupArea: applicationData?.totalBuildupArea ?? "",
      totalNumberOfOccupants: applicationData?.totalNumberOfOccupants ?? "",
      expectedConnectionStartDate: "",
      expectedConnectionEndDate: "",
      dcsDemand: {
        id: applicationData?.dcsDemand?.id ?? 0,
        contractDemand: applicationData?.dcsDemand?.contractDemand ?? "",
        ultimateDemand: applicationData?.dcsDemand?.ultimateDemand ?? "",
        waterFlowRate: applicationData?.dcsDemand?.waterFlowRate ?? "",
        chilledWaterLineDiameter:
          applicationData?.dcsDemand?.chilledWaterLineDiameter ?? "",
      },
      waterSewageDemand: {
        waterDemandId: applicationData?.waterDemand?.id ?? 0,
        sewageDemandId: applicationData?.sewageDemand?.id ?? 0,
        waterDemand:
          applicationData?.waterDemand?.waterDemand ??
          applicationData?.sewageDemand?.waterDemand ??
          0,
        flushing:
          applicationData?.waterDemand?.flushing ??
          applicationData?.sewageDemand?.flushing ??
          0,
        domestic:
          applicationData?.waterDemand?.domestic ??
          applicationData?.sewageDemand?.domestic ??
          0,
        gardening:
          applicationData?.waterDemand?.gardening ??
          applicationData?.sewageDemand?.gardening ??
          0,
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.consumerDuesOutstanding
          : "",
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding: applicationData?.declaration
          ? !!applicationData.declaration.premisesDuesOutstanding
          : "",
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
      },
      extraValues: {
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
        requiredDocumentsCount: applicationData?.requiredDocumentsCount ?? 0,
        proofOfOwnershipTypes: [],
        applicantType: applicantTypes ?? [],
        premiseTypes: [],
      },
      proofOfOwnershipDescription: "",
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      isDraft: true,
    }),
    [applicationData, applicantTypes]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getPermanentConnectionSchema[currentStep],

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createPermanentConnection(values);
      } else {
        setCurrentStep((prev) => prev + 1);
        actions.setTouched({});
      }
    },
  });

  const { setFieldValue, values, handleSubmit, setFieldTouched, errors } =
    formik;

  const {
    utilities,
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Applicant Details"),
        isCompleted: currentStep >= 1,
        component: (
          <ApplicantDetails
            formik={formik}
            isOtherUtilities
            isUtilityFieldRequired={
              applicationData?.applicationStatus?.name?.toLowerCase() ===
                "saved" || !applicationData
            }
          />
        ),
      },
      {
        id: 2,
        name: t("Authorized Person Details"),
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: t("Utility Address"),
        isCompleted: currentStep >= 3,
        component: (
          <AddressForPower formik={formik} floorOptions={floorOptions} />
        ),
      },
      {
        id: 4,
        name: t("Contract Demands"),
        isCompleted: currentStep >= 4,
        component: <UtilitiesFields formik={formik} />,
      },
      {
        id: 5,
        name: t("Document to Submit"),
        isCompleted: currentStep >= 5,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 6,
        name: t("Declaration"),
        isCompleted: currentStep === 6,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik, floorOptions, applicationData]
  );

  const createPermanentConnection = async (values, isDraft = false) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";

    const otherZone = values.utilityAddress.otherZone;
    const otherBlock = values.utilityAddress.otherBlock;
    const otherRoad = values.utilityAddress.otherRoad;
    const otherBuilding = values.utilityAddress.otherBuilding;
    const cityId = values.applicant.city?.id ?? "";

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(
      {
        ...payloadWithUpdatedDocumentKeys,
        applicant: {
          ...payloadWithUpdatedDocumentKeys.applicant,
          alternateEmail: payloadWithUpdatedDocumentKeys.applicant
            .alternateEmail.length
            ? payloadWithUpdatedDocumentKeys.applicant.alternateEmail.join(", ")
            : "",
          cityId,
        },
        expectedConnectionStartDate: values.expectedConnectionStartDate
          ? values.expectedConnectionStartDate
              .startOf("month")
              .format("YYYY-MM-DD")
          : "",
        expectedConnectionEndDate: values.expectedConnectionEndDate
          ? values.expectedConnectionEndDate
              .startOf("month")
              .format("YYYY-MM-DD")
          : "",
        utilityAddress: {
          ...payloadWithUpdatedDocumentKeys.utilityAddress,
          zoneName:
            payloadWithUpdatedDocumentKeys.utilityAddress.zoneName === "other"
              ? otherZone
              : payloadWithUpdatedDocumentKeys.utilityAddress.zoneName,
          blockName:
            payloadWithUpdatedDocumentKeys.utilityAddress.blockName === "other"
              ? otherBlock
              : payloadWithUpdatedDocumentKeys.utilityAddress.blockName,
          roadName:
            payloadWithUpdatedDocumentKeys.utilityAddress.roadName === "other"
              ? otherRoad
              : payloadWithUpdatedDocumentKeys.utilityAddress.roadName,
          buildingName:
            payloadWithUpdatedDocumentKeys.utilityAddress.buildingName ===
            "other"
              ? otherBuilding
              : payloadWithUpdatedDocumentKeys.utilityAddress.buildingName,
          floors: payloadWithUpdatedDocumentKeys.utilityAddress.floors.map(
            ({ label, value }) => ({
              id: value,
              name: label,
            })
          ),
        },
        isDraft,
      },
      [
        "applicant.city",
        "utilityAddress.otherZone",
        "utilityAddress.otherBlock",
        "utilityAddress.otherRoad",
        "utilityAddress.otherBuilding",
        "authorizedPerson.isAuthorizedPerson",
        "authorizedPerson.isUnRegisteredPerson",
        "declaration.isConsumerDuesOutstanding",
        "authorizedPerson.isEditablePerson",
        "declaration.isPremisesDuesOutstanding",
        "declaration.isFirmDuesOutstanding",
        "proofOfOwnershipDescription",
        "waterSewageDemand",
        "documents",
        "extraValues",
        imageKeyToRemove,
      ]
    );

    if (
      values.utilities.find(
        ({ value }) => value === getOptionValue("utilityOptions", "Sewage")
      )
    ) {
      applicationDetails.sewageDemand = {
        id: values.waterSewageDemand.sewageDemandId ?? 0,
        waterDemand: values.waterSewageDemand.waterDemand,
        flushing: values.waterSewageDemand.flushing,
        domestic: values.waterSewageDemand.domestic,
        gardening: values.waterSewageDemand.gardening,
      };
    }
    if (
      values.utilities.find(
        ({ value }) => value === getOptionValue("utilityOptions", "Water")
      )
    ) {
      applicationDetails.waterDemand = {
        id: values.waterSewageDemand.waterDemandId ?? 0,
        waterDemand: values.waterSewageDemand.waterDemand,
        flushing: values.waterSewageDemand.flushing,
        domestic: values.waterSewageDemand.domestic,
        gardening: values.waterSewageDemand.gardening,
      };
    }

    const filteredValues = filterEmptyStrings(applicationDetails);

    // remove entries that contain only id field
    const finalPayload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();
    generateFormData(formData, {
      ...finalPayload,
      declaration: filteredValues.declaration,
      utilities: values.utilities.map(({ value }) => value),
    });
    try {
      const res = await submitApplication(formData);
      if (isDraft) {
        temporaryConnectionValues.forEach(({ setKey, getKey, defaultValue }) =>
          setFieldValue(setKey, get(res.data, getKey) ?? defaultValue)
        );
        setFieldValue("proofOfOwnershipDescription", "");
      } else {
        // If submitted then navigate to my-request
        handleClose();
        if (!applicationData) {
          navigate(myOtherRequestRoute);
        }
      }
    } catch (error) {}
  };

  const handleDraft = async () => {
    // if (values.applicant.isEmailVerified) {
    const declarationFields = [
      {
        value: values.declaration.isConsumerDuesOutstanding,
        key: "declaration.consumerDuesOutstanding",
      },
      {
        value: values.declaration.isPremisesDuesOutstanding,
        key: "declaration.premisesDuesOutstanding",
      },
      {
        value: values.declaration.isFirmDuesOutstanding,
        key: "declaration.firmDuesOutstanding",
      },
    ];
    if (currentStep === 4) {
      if (!errors.proofOfOwnershipDescription) {
        createPermanentConnection(values, true);
      }
    } else if (currentStep === steps.length - 1) {
      declarationFields.forEach(({ value, key }) => {
        if (value) {
          setFieldTouched(key, true);
        }
      });
      if (!errors.declaration) {
        createPermanentConnection(values, true);
      }
    } else {
      createPermanentConnection(values, true);
    }
  };

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 4 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const getApplicationDataByApplicationId = async (editApplicationQuery) => {
    try {
      await getApplicationData(editApplicationQuery);
    } catch (error) {}
  };

  useEffect(() => {
    if (
      !utilities?.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      )
    ) {
      resetFieldValue(setFieldValue, [
        "utilityAddress.dcsDomesticPremiseType",
        "utilityAddress.dcsIndividualPremiseType",
      ]);
    }
  }, [utilities]);

  useEffect(() => {
    if (editApplicationQuery) {
      getApplicationDataByApplicationId(editApplicationQuery);
    }
  }, [editApplicationQuery]);

  // set initial floors
  useEffect(() => {
    const initialFloors = applicationData?.utilityAddress?.floors || [];

    if (floorOptions.length && initialFloors.length) {
      setFieldValue(
        "utilityAddress.floors",
        floorOptions.filter((option) =>
          initialFloors.some((floor) => floor.id === option.value)
        )
      );
    }
  }, [floorOptions, applicationData]);

  return (
    <div className={!editApplicationQuery ? "h-100" : ""}>
      <Backdrop open={isApplicationDataLoading} />

      {!editApplicationQuery && (
        <BreadCrumb
          title={t("Temporary Connection")}
          pageTitle={t("Other Applications")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          handleDraft={handleDraft}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          isLoading={isLoading}
          formik={formik}
          validateDocument={validateDocument}
          isDraftDisabled={
            !!editApplicationQuery &&
            applicationData?.applicationStatus?.name?.toLowerCase() !== "saved"
          }
        />
      </div>
    </div>
  );
};

OtherUtilityTemporaryConnection.propTypes = {
  editApplicationQuery: PropTypes.object,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(OtherUtilityTemporaryConnection);
